import { RequiredAsterisk } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { ReactNode, useId } from 'react';
import { cn } from 'utils/cn';

interface Props<T extends string> {
    value: T | null;
    onChange: (value: T) => void;
    options: { value: T; label: ReactNode }[];
    label: ReactNode;
    description?: ReactNode;
    inline?: boolean;
    required?: boolean;
    disabled?: boolean;
}

/**
 * A question answered by one of a few options, with none chosen until the user picks one. It never refuses an empty
 * answer: a blank on the client's form is recorded as a blank.
 */
export function AnswerRadio<T extends string>({
    value,
    onChange,
    options,
    label,
    description,
    inline,
    required,
    disabled,
}: Props<T>) {
    const id = useId();
    return (
        <div className="flex flex-col gap-1.5">
            <div id={`${id}-label`} className={FIELD_LABEL_CLASS}>
                {label}
                {required ? <RequiredAsterisk /> : null}
            </div>
            {description ? (
                <p id={`${id}-description`} className="text-caption text-foreground-secondary m-0">
                    {description}
                </p>
            ) : null}
            <RadioGroup
                value={value}
                onValueChange={(next) => onChange(next as T)}
                disabled={disabled}
                aria-labelledby={`${id}-label`}
                aria-describedby={description ? `${id}-description` : undefined}
                aria-required={required || undefined}
                className={cn('gap-0', inline && 'flex flex-row flex-wrap gap-x-3.5')}
            >
                {options.map((option) => (
                    <label
                        key={option.value}
                        className={cn(
                            'flex min-h-8.5 w-fit flex-row items-center gap-2 text-sm',
                            disabled ? 'text-fg-disabled cursor-not-allowed' : 'text-fg cursor-pointer'
                        )}
                    >
                        <RadioGroupItem value={option.value} />
                        <span>{option.label}</span>
                    </label>
                ))}
            </RadioGroup>
        </div>
    );
}
